import { useIntl } from "react-intl";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useEffect, useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./columns";
import { useNavigate } from "react-router-dom";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { TableHeaderFilter } from "@lib/types/Api";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { TableProps } from "antd";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { Product } from "@lib/types/Billing";
import { ProductModal } from "@lib/components/PageComponents/Product";
import { useProductQueries } from "@lib/apiQueries";

const ProductsList = <T extends Product>() => {
  const { messages } = useIntl();
  const navigate = useNavigate();

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
  });

  const {
    productsQuery,
    addProductMutation,
    updateProductMutation,
    deleteProductMutation,
  } = useProductQueries({
    params,
    messages,
  });
  const { data, isLoading, isFetching } = productsQuery;
  const apiData = data?.data?.data;

  useEffect(() => {
    if (addProductMutation.isSuccess) {
      toggle(false);
    }
  }, [addProductMutation.isSuccess]);

  const [openModal, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<T>();

  useEffect(() => {
    if (!openModal) setSelected(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelectItem = (product: T) => {
    setSelected(product);
    toggle(true);
  };

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<any>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        handleSelectItem,
        navigate,
        deleteQuery: deleteProductMutation,
        openModal,
        apiData,
        params,
        messages,
      }),
    [apiData, deleteProductMutation, openModal, handleSelectItem, params],
  );

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter.map((f) => ({
      key: f.split("||")[0],
      title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
      operator: f.split("||")[1],
      value: f.split("||")[2],
    })) as TableHeaderFilter[];
  }, [params?.filter]);

  return (
    <AppsContainer
      fullView={true}
      title={messages["product.list"] as string}
      cardProps={{
        borderFull: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={productsQuery}
          plusProps={{
            onClick: () => toggle(true),
            disabled: isLoading,
            loading: isLoading,
          }}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          onChange={onChange}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          size="small"
        />
      </AppsContent>

      <ProductModal
        mutationQuery={selected ? updateProductMutation : addProductMutation}
        openModal={openModal}
        toggleModal={toggle}
        product={selected}
      />
    </AppsContainer>
  );
};

export default ProductsList;
